import { MediaResponse } from "../types";

// Shared file extension constants
export const FILE_EXTENSIONS = {
  IMAGE: ["jpg", "jpeg", "png", "gif", "webp", "bmp"] as string[],
  VIDEO: ["mp4", "webm", "ogg", "mov", "mts", "m4v", "vob", "mkv", "avi"] as string[],
  AUDIO: ["mp3", "wav", "ogg", "aac"] as string[],
  PDF: ["pdf"] as string[],
  // Tabular data files that can be rendered as a browsable table via the csv-data endpoint.
  TABLE: ["csv"] as string[],
  // DOCUMENT also includes 3D scan/model formats; we treat these as downloadable files
  // (preview is typically handled by an external viewer / embed, not by the browser directly).
  DOCUMENT: ["doc", "docx", "txt", "rtf", "glb", "gltf", "usdz", "obj", "stl", "ply"] as string[]
} as const;

// Legacy export for backward compatibility
export const MEDIA_TYPES = FILE_EXTENSIONS;

// Utility function to generate case-variant extensions for upload validation
export const generateCaseVariantExtensions = (extensions: string[]): string[] => {
  const variants: string[] = [];
  extensions.forEach((ext) => {
    variants.push(`.${ext.toLowerCase()}`);
    variants.push(`.${ext.toUpperCase()}`);
  });
  return variants;
};

export const getMediaType = (item: MediaResponse | null): string => {
  if (!item) return "unknown";

  // Use explicit media_type if available
  if (item.media_type) return item.media_type;

  // Try to infer from MIME type
  if (item.remote_details?.mime) {
    const mime = item.remote_details.mime.toLowerCase();
    if (mime.startsWith("image/")) return "photo";
    if (mime.startsWith("video/")) return "video";
    if (mime.startsWith("audio/")) return "audio";
    if (mime === "application/pdf") return "pdf";
  }

  // Try to infer from filename extension
  if (item.original_file?.filename) {
    const ext = item.original_file.filename.split(".").pop()?.toLowerCase() || "";
    if (MEDIA_TYPES.IMAGE.includes(ext)) return "photo";
    if (MEDIA_TYPES.VIDEO.includes(ext)) return "video";
    if (MEDIA_TYPES.AUDIO.includes(ext)) return "audio";
    if (MEDIA_TYPES.PDF.includes(ext)) return "pdf";
    if (MEDIA_TYPES.TABLE.includes(ext)) return "csv";
    if (MEDIA_TYPES.DOCUMENT.includes(ext)) return "document";
  }

  return "unknown";
};

export const isImageMediaItem = (item: MediaResponse | null): boolean => {
  return getMediaType(item) === "photo";
};

// Video formats that browsers cannot play natively and need server-side conversion
// NOTE: AVI (video/x-msvideo) is NOT natively playable in any major browser, so it
// must be converted server-side just like MTS/VOB/MKV.
export const UNSUPPORTED_BROWSER_FORMATS = ["mts", "MTS", "m2ts", "M2TS", "vob", "VOB", "mkv", "MKV", "avi", "AVI"] as const;

// Helper function to check if a file needs server-side conversion before browser playback
export const needsServerConversion = (file: File): boolean => {
  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  return UNSUPPORTED_BROWSER_FORMATS.some((format) => format.toLowerCase() === extension);
};

// Helper function to check if a file extension is browser-playable
// AVI is intentionally excluded: browsers cannot decode video/x-msvideo natively.
export const isBrowserPlayableVideo = (filename: string): boolean => {
  const extension = filename.split(".").pop()?.toLowerCase() || "";
  const browserPlayableFormats = ["mp4", "webm", "ogg", "mov", "m4v"];
  return browserPlayableFormats.includes(extension);
};
